import React, { useEffect, useState } from "react";
import { Trash2, ExternalLink, X, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import ImagePicker, { absolutizeUrl } from "@/components/ImagePicker";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import DecorationLayer from "@/components/DecorationLayer";

const PARTNERS = [
    { name: "Argentario Bike", url: "https://www.argentariobike.it/", tagIt: "Gruppo ciclistico", tagEn: "Cycling crew", logo: "" },
    { name: "Tuscan Hill Enduro", url: "https://www.tuscanhillenduro.com/", tagIt: "Guide MTB", tagEn: "MTB guides", logo: "https://customer-assets.emergentagent.com/job_argentario-enduro/artifacts/kq5u6iaq_Bazaart_409D2429-99C6-48AA-9ED2-0F5F5333204C.png" },
    { name: "Goat Trails", url: "https://goattrails.swiss/", tagIt: "Progetto trail", tagEn: "Trail project", logo: "" },
    { name: "Bike Point", url: "https://www.bikepointorbetello.it/", tagIt: "Bike shop & officina", tagEn: "Bike shop & workshop", logo: "https://customer-assets.emergentagent.com/job_argentario-enduro/artifacts/b4s4b62f_IMG_0417.jpeg" },
];

const INSTAGRAM_URL = "https://www.instagram.com/abnormalworld/";

/* Editorial section header — matches the Home identity */
function SectionHeader({ n, title, right }) {
    return (
        <div className="flex items-baseline justify-between border-b border-white/15 pb-3 mb-8 flex-wrap gap-3">
            <div className="flex items-baseline gap-3">
                <span className="font-vt text-[11px] md:text-sm tracking-[0.35em] text-abnormal-acid">[ N° {n} ]</span>
                <span className="w-4 h-px bg-abnormal-acid/60" />
                <span className="font-vt text-[11px] md:text-sm tracking-[0.4em] uppercase text-abnormal-paper">
                    {title}
                </span>
            </div>
            {right}
        </div>
    );
}

function PhotoLightbox({ photo, onClose, lang }) {
    useEffect(() => {
        if (!photo) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [photo, onClose]);

    if (!photo) return null;
    const title = photo.rider_name || "";
    const caption = (lang === "en" && photo.caption_en) ? photo.caption_en : (photo.caption || "");

    return (
        <div
            data-testid="photo-lightbox"
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
        >
            <button
                onClick={onClose}
                data-testid="photo-lightbox-close"
                aria-label="Close"
                className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 flex items-center justify-center bg-black border border-abnormal-acid/50 hover:border-abnormal-acid hover:text-abnormal-acid transition-colors"
            >
                <X size={20} />
            </button>

            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl grid lg:grid-cols-[1.4fr_1fr] gap-0 border border-white/10 bg-abnormal-charcoal relative">
                <span className="absolute top-2 left-2 w-4 h-4 border-t border-l border-abnormal-acid pointer-events-none" />
                <span className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-abnormal-acid pointer-events-none" />
                <div className="bg-black flex items-center justify-center max-h-[85vh] overflow-hidden">
                    <img
                        src={absolutizeUrl(photo.image_url)}
                        alt={title}
                        className="w-full h-full object-contain max-h-[85vh]"
                    />
                </div>
                <div className="p-6 md:p-8 flex flex-col">
                    <p className="font-vt text-[10px] tracking-[0.35em] uppercase text-abnormal-acid mb-3">
                        // rider · argentario
                    </p>
                    {title && (
                        <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight text-abnormal-paper">
                            {title}<span className="text-abnormal-acid">.</span>
                        </h3>
                    )}
                    {caption ? (
                        <p className="mt-5 text-abnormal-paper leading-relaxed whitespace-pre-wrap text-base">{caption}</p>
                    ) : (
                        <p className="mt-5 text-abnormal-paperDim/50 italic text-sm">—</p>
                    )}
                    {photo.created_at && (
                        <p className="mt-auto pt-6 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paperDim/70 flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                            {new Date(photo.created_at).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Community() {
    const { user } = useAuth();
    const { t, lang } = useLang();

    const [riderPhotos, setRiderPhotos] = useState([]);
    const [riderUrl, setRiderUrl] = useState("");
    const [riderName, setRiderName] = useState("");
    const [riderCaption, setRiderCaption] = useState("");
    const [postingRider, setPostingRider] = useState(false);
    const [openPhoto, setOpenPhoto] = useState(null);

    const loadAll = async () => {
        try {
            const rg = await api.get("/gallery/riders");
            setRiderPhotos(rg.data || []);
        } catch {
            setRiderPhotos([]);
        }
    };
    useEffect(() => { loadAll(); }, []);

    const submitRider = async (e) => {
        e.preventDefault();
        if (!riderUrl.trim()) { toast.error(t("common.uploadOrPaste")); return; }
        setPostingRider(true);
        try {
            await api.post("/gallery/riders", {
                image_url: riderUrl.trim(),
                rider_name: riderName.trim(),
                caption: riderCaption.trim(),
            });
            setRiderUrl(""); setRiderName(""); setRiderCaption("");
            toast.success("OK");
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally { setPostingRider(false); }
    };
    const deleteRider = async (id) => {
        if (!window.confirm("Eliminare questa foto?")) return;
        try { await api.delete(`/gallery/riders/${id}`); toast.success("OK"); loadAll(); }
        catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
    };

    const isAdmin = user && user.role === "admin";

    return (
        <div data-testid="community-page" className="bg-black text-abnormal-paper relative">
            <DecorationLayer page="community" />
            <SEO
                title={lang === "en" ? "Community" : "Community"}
                description={lang === "en"
                    ? "The Abnormal community: rider photos, partners and the people building the Argentario MTB scene."
                    : "La community Abnormal: foto dei rider, partner e le persone che stanno costruendo la scena MTB Argentario."}
                path="/community"
            />

            {/* ═══════════ 01 · HEADER — shared PageHeader ═══════════ */}
            <PageHeader
                label="COMMUNITY · ARGENTARIO CREW"
                title="The crew"
                tagline={["People", "Partners", lang === "en" ? "Real Argentario" : "Argentario vero"]}
                sub={lang === "en"
                    ? "Photos, riders, local realities. One place to tell the Enduro side of Monte Argentario."
                    : "Foto, rider, realtà locali. Un posto solo per raccontare l'enduro al Monte Argentario."}
                size="xl"
            />

            {/* ═══════════ 02 · PARTNERS ═══════════ */}
            <section data-testid="partners-section" className="relative border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <SectionHeader
                        n="01"
                        title={lang === "en" ? "Partners in crime" : "Insieme a noi"}
                        right={
                            <span className="font-vt text-[10px] md:text-sm tracking-[0.3em] uppercase text-abnormal-paperDim/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                {PARTNERS.length} {lang === "en" ? "allies" : "alleati"}
                            </span>
                        }
                    />
                    <p className="max-w-3xl text-abnormal-paperDim text-base md:text-lg leading-relaxed mb-8">
                        {lang === "en"
                            ? "Argentario Bike, Tuscan Hill Enduro, Goat Trails and Bike Point stand together with Abnormal for Enduro at Monte Argentario."
                            : "Argentario Bike, Tuscan Hill Enduro, Goat Trails e Bike Point sostengono insieme ad Abnormal l'enduro al Monte Argentario."}
                    </p>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {PARTNERS.map((p, i) => (
                            <a
                                key={p.url}
                                href={p.url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="group relative bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors p-5 flex flex-col"
                            >
                                {/* Wireframe corner brackets on hover */}
                                <span className="absolute top-1 left-1 w-3 h-3 border-t border-l border-abnormal-acid opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-abnormal-acid opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex items-start justify-between gap-3">
                                    {p.logo ? (
                                        <img src={p.logo} alt={p.name} className="w-14 h-14 object-contain bg-black border border-white/10" loading="lazy" />
                                    ) : (
                                        <span className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-acid">
                                            N° 0{i + 1}
                                        </span>
                                    )}
                                    <ExternalLink size={14} className="text-abnormal-paperDim/60 group-hover:text-abnormal-acid transition-colors" />
                                </div>
                                <p className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-paperDim/70 mt-3 flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                    {lang === "en" ? p.tagEn : p.tagIt}
                                </p>
                                <p className="font-display text-2xl md:text-3xl uppercase leading-tight mt-2 text-abnormal-paper">
                                    {p.name}<span className="text-abnormal-acid">.</span>
                                </p>
                                <p className="font-vt text-[11px] text-abnormal-paperDim/60 mt-2 break-all">{p.url.replace(/^https?:\/\//, "")}</p>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════ 03 · RIDER GALLERY (curated) ═══════════ */}
            <section data-testid="rider-gallery-section" className="relative border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
                    <SectionHeader
                        n="02"
                        title={lang === "en" ? "Local riders" : "Rider locali"}
                        right={
                            <div className="flex items-center gap-3">
                                {isAdmin && (
                                    <span className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-acid border border-abnormal-acid/60 px-2 py-0.5">
                                        ADMIN MODE
                                    </span>
                                )}
                                <span className="font-vt text-[10px] md:text-sm tracking-[0.3em] uppercase text-abnormal-paperDim/70 flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                    {riderPhotos.length} {lang === "en" ? "portraits" : "ritratti"}
                                </span>
                            </div>
                        }
                    />

                    <p className="max-w-3xl text-abnormal-paperDim text-base md:text-lg leading-relaxed mb-8">
                        {lang === "en"
                            ? "The Argentario scene: rider portraits curated by Abnormal. No filters, no fake crowds."
                            : "La scena dell'Argentario: ritratti di rider curati da Abnormal. Nessun filtro, nessuna finta folla."}
                    </p>

                    {isAdmin && (
                        <div data-testid="admin-tools-row" className="mb-8 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={async () => {
                                    const tid = toast.loading("Traduzione in corso (può richiedere 30-60s)…");
                                    try {
                                        const r = await api.post("/admin/translate-captions");
                                        toast.success(`Tradotte ${r.data.translated_community} community + ${r.data.translated_rider} rider`, { id: tid });
                                        loadAll();
                                    } catch (err) { toast.error(err.response?.data?.detail || "Errore", { id: tid }); }
                                }}
                                data-testid="translate-captions-btn"
                                className="font-vt text-[11px] uppercase tracking-[0.3em] font-bold border border-abnormal-acid/60 text-abnormal-acid px-3 py-2 hover:bg-abnormal-acid hover:text-black transition-colors"
                            >
                                ⚡ Traduci caption EN
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const tid = toast.loading("Traduzione trail in corso…");
                                    try {
                                        await api.post("/admin/translate-trails");
                                        toast.success("Trail tradotti", { id: tid });
                                    } catch (err) { toast.error(err.response?.data?.detail || "Errore", { id: tid }); }
                                }}
                                data-testid="translate-trails-btn"
                                className="font-vt text-[11px] uppercase tracking-[0.3em] font-bold border border-white/20 text-abnormal-paperDim px-3 py-2 hover:border-abnormal-acid hover:text-abnormal-acid transition-colors"
                            >
                                ⚡ Traduci trail EN
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const tid = toast.loading("Traduzione guide in corso (60-90s)…");
                                    try {
                                        const r = await api.post("/admin/translate-guides");
                                        const ok = (r.data.result || []).filter(x => x.translated || x.skipped).length;
                                        toast.success(`Guide processate: ${ok}`, { id: tid });
                                    } catch (err) { toast.error(err.response?.data?.detail || "Errore", { id: tid }); }
                                }}
                                data-testid="translate-guides-btn"
                                className="font-vt text-[11px] uppercase tracking-[0.3em] font-bold border border-white/20 text-abnormal-paperDim px-3 py-2 hover:border-abnormal-acid hover:text-abnormal-acid transition-colors"
                            >
                                ⚡ Traduci guide EN
                            </button>
                        </div>
                    )}

                    {isAdmin && (
                        <form onSubmit={submitRider} data-testid="rider-upload-form" className="mb-10 grid md:grid-cols-[1.4fr_1fr] gap-6 items-start border border-abnormal-acid/40 bg-abnormal-acid/[0.03] p-6 relative">
                            <span className="absolute top-1 left-1 w-3 h-3 border-t border-l border-abnormal-acid" />
                            <span className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-abnormal-acid" />
                            <div>
                                <label className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-acid mb-2 block">// {lang === "en" ? "Rider photo" : "Foto rider"}</label>
                                <ImagePicker value={riderUrl} onChange={setRiderUrl} testidPrefix="rider-photo" />
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-acid mb-2 block">// {lang === "en" ? "Rider name" : "Nome rider"}</label>
                                    <input value={riderName} onChange={(e) => setRiderName(e.target.value)} data-testid="rider-name-input" maxLength={60}
                                        className="w-full bg-black border border-white/20 focus:border-abnormal-acid outline-none px-3 py-2 text-sm" />
                                </div>
                                <div>
                                    <label className="font-vt text-[10px] uppercase tracking-[0.3em] text-abnormal-acid mb-2 block">// {lang === "en" ? "Caption (optional)" : "Descrizione (opzionale)"}</label>
                                    <textarea rows={4} maxLength={800} value={riderCaption} onChange={(e) => setRiderCaption(e.target.value)} data-testid="rider-caption-input"
                                        className="w-full bg-black border border-white/20 focus:border-abnormal-acid outline-none px-3 py-2 text-sm min-h-[90px]" />
                                </div>
                                <button type="submit" data-testid="rider-submit-btn" disabled={postingRider || !riderUrl}
                                    className="w-full bg-abnormal-acid text-abnormal-paper uppercase tracking-wider font-bold py-2.5 disabled:opacity-50 hover:bg-abnormal-paper hover:text-abnormal-acid transition-colors text-sm border-b-2 border-abnormal-acid">
                                    {postingRider ? (lang === "en" ? "Uploading…" : "Carico…") : (lang === "en" ? "Add rider" : "Aggiungi rider")}
                                </button>
                            </div>
                        </form>
                    )}

                    {riderPhotos.length === 0 ? (
                        <div className="border border-white/10 p-10 flex flex-col items-center gap-3">
                            <p className="text-abnormal-paperDim text-sm uppercase tracking-[0.25em]">
                                {isAdmin
                                    ? (lang === "en" ? "No photos yet. Add one from the form above." : "Nessuna foto ancora. Aggiungila dal form qui sopra.")
                                    : (lang === "en" ? "Gallery updating." : "Galleria in aggiornamento.")}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {riderPhotos.map((p, i) => {
                                /* Rotating BRAND stamps — clearly editorial, not fake rider info */
                                const stamps = ["ABNORMAL WORLD", "STAY WEIRD", "GROW WILD", "RIDE ABNORMAL"];
                                const stamp = stamps[i % stamps.length];
                                return (
                                    <button
                                        type="button"
                                        key={p.id}
                                        onClick={() => setOpenPhoto(p)}
                                        data-testid={`rider-card-${p.id}`}
                                        className="relative group aspect-[4/5] overflow-hidden border border-white/10 hover:border-abnormal-acid transition-colors text-left"
                                    >
                                        <img
                                            src={absolutizeUrl(p.image_url)}
                                            alt={p.rider_name || "rider"}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                                        {/* Top-left N° label — real editorial numbering */}
                                        <div className="absolute top-3 left-3 flex items-center gap-1.5 font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paper/85">
                                            <span className="text-abnormal-acid">N° {String(i + 1).padStart(2, "0")}</span>
                                            <span className="w-3 h-px bg-abnormal-acid/60" />
                                            <span className="opacity-70">RIDER</span>
                                        </div>
                                        {/* Vertical BRAND stamp — clearly editorial, not fake info */}
                                        <div
                                            className="absolute top-14 right-3 font-vt text-[10px] tracking-[0.5em] uppercase text-abnormal-acid/80 pointer-events-none select-none"
                                            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                                            aria-hidden="true"
                                        >
                                            <span className="text-abnormal-acid">·</span> {stamp}
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
                                            {p.rider_name && (
                                                <p className="font-display text-2xl md:text-3xl uppercase leading-none text-abnormal-paper">
                                                    {p.rider_name}<span className="text-abnormal-acid">.</span>
                                                </p>
                                            )}
                                            {p.caption && (
                                                <p className="text-sm text-abnormal-paperDim mt-2 line-clamp-2 max-w-[85%]">
                                                    {(lang === "en" && p.caption_en) ? p.caption_en : p.caption}
                                                </p>
                                            )}
                                        </div>
                                        {isAdmin && (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) => { e.stopPropagation(); deleteRider(p.id); }}
                                                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteRider(p.id); } }}
                                                data-testid={`rider-delete-${p.id}`}
                                                className="absolute top-2 right-2 bg-black/70 border border-white/20 p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-abnormal-acid cursor-pointer"
                                                aria-label="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* ═══════════ 04 · SHARE ON INSTAGRAM CTA ═══════════ */}
            <section data-testid="ig-cta-section" className="relative border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 relative">
                    <SectionHeader
                        n="03"
                        title={lang === "en" ? "Your shots" : "Le tue foto"}
                        right={
                            <span className="font-vt text-[10px] md:text-sm tracking-[0.3em] uppercase text-abnormal-paperDim/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                @abnormalworld
                            </span>
                        }
                    />

                    <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-start">
                        <div>
                            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight text-abnormal-paper">
                                {lang === "en" ? "Tag us." : "Taggaci."}<br />
                                <span className="text-abnormal-acid">
                                    {lang === "en" ? "We'll repost the good ones." : "Ripostiamo le migliori."}
                                </span>
                            </h2>
                            <p className="mt-6 max-w-xl text-abnormal-paperDim leading-relaxed">
                                {lang === "en"
                                    ? "No login. No upload form. No spam. Post your Argentario photos on Instagram, tag @abnormalworld or use #abnormalworld. If they're good, they end up here — curated."
                                    : "Niente login. Nessun form di upload. Zero spam. Pubblica le tue foto dell'Argentario su Instagram, tagga @abnormalworld o usa #abnormalworld. Se sono forti, finiscono qui — selezionate."}
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <a
                                    href={INSTAGRAM_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="ig-follow-cta"
                                    className="group bg-abnormal-acid text-abnormal-paper px-6 py-3 font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 hover:bg-abnormal-paper hover:text-abnormal-acid transition-colors border-b-2 border-abnormal-acid"
                                >
                                    <Instagram size={16} className="text-abnormal-acid group-hover:text-abnormal-acid transition-colors" />
                                    {lang === "en" ? "Follow @abnormalworld" : "Segui @abnormalworld"}
                                    <ArrowRight size={14} className="text-abnormal-acid group-hover:text-abnormal-acid group-hover:translate-x-0.5 transition-all" />
                                </a>
                                <span className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paperDim/70 flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-abnormal-acid" />
                                    #abnormalworld
                                </span>
                            </div>

                            {/* Rules — notebook-style checklist */}
                            <ul className="mt-10 space-y-3 max-w-md">
                                {[
                                    lang === "en" ? "Real Argentario photos only" : "Solo foto vere all'Argentario",
                                    lang === "en" ? "Riders, trails, terrain, moments" : "Rider, trail, terreno, momenti",
                                    lang === "en" ? "Tag @abnormalworld or #abnormalworld" : "Tagga @abnormalworld o #abnormalworld",
                                    lang === "en" ? "The good ones make it here" : "Le migliori arrivano qui",
                                ].map((rule) => (
                                    <li key={rule} className="flex items-start gap-3 font-vt text-sm text-abnormal-paper">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-abnormal-acid flex-shrink-0" />
                                        <span>{rule}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="relative">
                            <div className="border border-white/10 p-8 md:p-10">
                                <div className="flex flex-col items-center gap-4">
                                    <p className="font-display text-3xl uppercase text-abnormal-paper text-center leading-none">
                                        {lang === "en" ? "No feed." : "Niente feed."}<br />
                                        <span className="text-abnormal-acid">{lang === "en" ? "Just trails." : "Solo trail."}</span>
                                    </p>
                                    <p className="font-vt text-xs tracking-[0.3em] uppercase text-abnormal-paperDim/70">
                                        {lang === "en" ? "// curated · not user-generated" : "// selezionate · non user-generated"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PhotoLightbox photo={openPhoto} onClose={() => setOpenPhoto(null)} lang={lang} />
        </div>
    );
}
